import { useState, type ReactNode } from 'react';
import { accessFrame, useAuthMe } from './api/hooks';
import type { WorkspaceRole } from './api/types';
import { HonestState } from './states/HonestState';
import { stateOfError } from './states/stateOf';
import { useElapsedLine } from './states/useElapsed';
import { AppShell } from './shell';
import { useView, type ViewId } from './router/useView';
import { AuthFrame } from './views/auth/AuthFrame';
import { AuthRouter } from './views/auth';
import { shouldBypassLocalAuth } from './views/auth/localAccess';
import { VIEW_COMPONENTS } from './views/registry';
import { DEV_VIEW_COMPONENTS, useDevViewId } from './views/devViews';
import { canAccessView, viewAccessReason } from './shell/destinations';

export function ViewAccessGate({
  view,
  role,
  enforce = true,
  children,
}: {
  readonly view: ViewId;
  readonly role: WorkspaceRole | null;
  readonly enforce?: boolean;
  readonly children: ReactNode;
}) {
  if (!enforce || canAccessView(view, role)) return children;
  return (
    <HonestState
      variant="Permission denied"
      title="Owner access required"
      reason={viewAccessReason(view)}
      nextAction={{ label: 'Open Research desk', href: '?view=desk' }}
    />
  );
}

// Boot: the access check decides between the sign-in door and the shell.
// checking renders a bounded Loading state (the client aborts at 10 s and
// retries at 1, 4 and 10 s); a 401 renders the door; a failure the backoff
// could not ride out keeps the shell if a session was already open in this
// page load, and only opens the door when none ever was.
export function App() {
  const { view } = useView();
  const devViewId = useDevViewId();
  const auth = useAuthMe();
  const localAccess = shouldBypassLocalAuth(import.meta.env.DEV, window.location.hostname);
  // Remembers that a session was open in this page load, so a later 401
  // renders the door with the "session ended" notice.
  const [hadSession, setHadSession] = useState(false);
  if (auth.access === 'signed_in' && !hadSession) setHadSession(true);

  const frame = localAccess ? 'shell' : accessFrame(auth.access, hadSession);
  if (frame === 'checking') return <AccessCheck />;

  if (frame === 'door') {
    return (
      <AuthRouter
        access={auth.access}
        accessError={auth.error}
        onRetryAccess={auth.refetch}
        hadSession={hadSession}
      />
    );
  }

  const accessRole = localAccess ? 'owner' : (auth.identity?.role ?? null);
  const View = (devViewId ? DEV_VIEW_COMPONENTS[devViewId] : undefined) ?? VIEW_COMPONENTS[view];
  return (
    <AppShell identity={auth.identity} accessRole={accessRole}>
      {frame === 'shell_degraded' ? (
        <AccessDegraded error={auth.error} onRetry={auth.refetch} />
      ) : null}
      <ViewAccessGate view={view} role={accessRole} enforce={devViewId === null}>
        <View />
      </ViewAccessGate>
    </AppShell>
  );
}

// The boot access check. Its own component so the elapsed counter can run:
// the contract's Loading row wants the seconds after 2 s, and feature
// requirements 26 forbids an endless loading state on the way in.
function AccessCheck() {
  const elapsed = useElapsedLine(true);
  return (
    <AuthFrame
      title="Opening Aleph Terminal"
      where="Access check"
      statusNote="Reading the session cookie in this browser"
    >
      <HonestState
        variant="Loading"
        reason="Checking whether this browser holds a workspace session (10 s limit, three retries)."
        detail={elapsed}
        nextAction={{ label: 'Reload', onSelect: () => window.location.reload() }}
      />
    </AuthFrame>
  );
}

// The access check stopped answering while the reader was already inside.
// The shell stays, the destination keeps whatever it has loaded, and this
// strip names the observed code and offers the retry. Swapping the whole
// terminal for the sign-in door on a transient 429 was the measured P0.
function AccessDegraded({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry: () => void;
}) {
  const state = stateOfError(error);
  return (
    <HonestState
      className="access-strip"
      variant={state.variant}
      title="Access check did not answer"
      reason={state.reason}
      detail="Your session is still the one this browser held; nothing has signed you out."
      nextAction={{ label: 'Retry access check', onSelect: onRetry }}
    />
  );
}
